import { store } from "../store";
import DataStructure from "./DataStructure";
import Address from "./Address";
import { deepFreeze } from "../util/misc";
import { isGeneralContact } from "../util/contactMatch";
import { defaults, isArray, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

//#region BillTo

export const BILL_TO_FIELDS = {
  type: "person",
  address: null,
};
deepFreeze(BILL_TO_FIELDS);

export const BILL_TO_TYPES = [
  { label: "Company", value: "company" },
  { label: "Person", value: "person" },
];
deepFreeze(BILL_TO_TYPES);

// Bill-to addresses offered for a sales lead's Received From contact, one per
// type. The company is the contact itself when it is a company's General
// Contact, whether or not the company has an account yet; otherwise it is the
// account the person belongs to.
export const billToOptionsFor = (contact) => {
  const options = { person: null, company: null };
  if (!contact) return options;
  const isCompany = isGeneralContact(contact);
  const company = isCompany
    ? contact
    : contact.company && contact.company.data
    ? contact.company
    : null;
  if (company && company.data.addresses.length) {
    options.company = { ...company.data.addresses[0], contact: company.id };
  }
  if (!isCompany && contact.data.addresses.length) {
    options.person = { ...contact.data.addresses[0], contact: contact.id };
  }
  return options;
};

export class BillTo extends DataStructure {
  constructor(data = {}) {
    // Backward compatibility
    if (isArray(data)) {
      data = data[0];
    }
    // A null/undefined soldTo (Sale's SALE_FIELDS default is null, and an empty
    // array collapses to undefined above) must yield an empty BillTo, not throw
    // — the `= {}` param default doesn't catch an explicit null, so `new
    // BillTo(null)` would hit `!data.address` on null. Reached whenever a Sale
    // is constructed before its soldTo loads (e.g. via Order.get sale).
    if (!data) {
      data = {};
    }
    if (!data.address) {
      data = {
        ...BILL_TO_FIELDS,
        address: data,
      };
    }

    super(defaults({}, data, BILL_TO_FIELDS));
  }

  get isValid() {
    return Boolean(this.data.type && this.address.isValid);
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : new Address();
  }

  get type() {
    return BILL_TO_TYPES.find((type) => type.value === this.data.type);
  }

  get contact() {
    return this.data.address && this.data.address.contact
      ? store.state.main.contacts[this.data.address.contact] || null
      : null;
  }

  // The customer Company account being billed, when the bill-to IS a company.
  // Company contacts share their company doc's id, so the contact id resolves
  // straight to the company doc — no need to trust `data.type`.
  get companyAccount() {
    const contactID = this.data.address && this.data.address.contact;
    return contactID ? store.state.main.companies[contactID] || null : null;
  }

  // Wholesale is a property of the account being billed, so only a company
  // bill-to can be wholesale. Billing a person who merely works for a
  // wholesale account is an ordinary commissioned sale — the wholesale margin
  // is extended to the account, not to its people. Keep in sync with
  // `resolveBillToWholesale` in functions/sales-helpers.js, which decides the
  // same thing server-side when the sale is created.
  get isWholesale() {
    const company = this.companyAccount;
    return Boolean(company && company.isWholesale);
  }

  get name() {
    const contact = this.contact;
    return contact ? contact.name : "";
  }

  get link() {
    return this.address.link;
  }

  toString(options) {
    return this.address.toString(options);
  }
}

//#region ShipTo

export const SHIP_TO_FIELDS = {
  type: "jobsite",
  address: null,
  pickup: false,
};
deepFreeze(SHIP_TO_FIELDS);

export const SHIP_TO_TYPES = [
  { label: "Jobsite", value: "jobsite" },
  { label: "Location", value: "location" },
  { label: "Contact", value: "contact" },
  { label: "Other", value: "other" },
];
deepFreeze(SHIP_TO_TYPES);

export class ShipTo extends DataStructure {
  constructor(data = {}) {
    super(defaults({}, data, SHIP_TO_FIELDS));
  }

  get isValid() {
    return Boolean(this.data.type && this.address.isValid);
  }

  isEqual(shipTo) {
    return isEmpty(diff(this.data, shipTo.data));
  }

  get address() {
    return this.data.address ? new Address(this.data.address) : new Address();
  }

  get type() {
    return SHIP_TO_TYPES.find((type) => type.value === this.data.type);
  }

  get contact() {
    return this.data.address && this.data.address.contact
      ? store.state.main.contacts[this.data.address.contact] || null
      : null;
  }

  get name() {
    const contact = this.contact;
    return contact ? contact.name : "";
  }

  get link() {
    return this.address.link;
  }

  toString(options) {
    return this.address.toString(options);
  }
}
